/**
 * Staff editor for ProEpoxy pack sizes, coverage and prices. Shown on the
 * "Pricing for Customer Quotes" page for the ProEpoxy tab. Each group is a
 * small table; add/remove rows, edit numbers, Save, or Reset to defaults.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Save, RotateCcw, Loader2, Layers } from 'lucide-react';
import API_URL from '@/config/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatApiError } from '@/utils/apiError';
import { fetchProEpoxyCatalogue } from './catalogueLoader';

const emptyRow = (columns) => Object.fromEntries(columns.map((c) => [c.field, c.type === 'bool' ? false : '']));

function GroupEditor({ group, onSaved }) {
  const [rows, setRows] = useState(group.packs);
  const [busy, setBusy] = useState(null);
  useEffect(() => { setRows(group.packs); }, [group]);
  const dirty = useMemo(() => JSON.stringify(rows) !== JSON.stringify(group.packs), [rows, group.packs]);

  const setCell = (i, field, value) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [field]: value } : row)));

  const save = async () => {
    setBusy('save');
    try {
      const res = await fetch(`${API_URL}/api/proepoxy/catalogue/${group.key}`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ packs: rows }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(body) || 'Save failed');
      onSaved(body);
      toast.success(`${group.label} saved — installers get the new packs on their next order.`);
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const reset = async () => {
    if (!window.confirm(`Reset "${group.label}" to the built-in defaults?`)) return;
    setBusy('reset');
    try {
      const res = await fetch(`${API_URL}/api/proepoxy/catalogue/${group.key}`, { method: 'DELETE', credentials: 'include' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(body) || 'Reset failed');
      onSaved(body);
      toast.success(`${group.label} reset to defaults.`);
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  return (
    <Card className="p-3 sm:p-4" data-testid={`pack-group-${group.key}`}>
      <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
        <div>
          <p className="font-bold text-gray-900 text-sm">{group.label}</p>
          <p className="text-[11px] text-gray-500">
            {group.is_default ? 'Built-in defaults' : `Edited ${group.updated_at ? new Date(group.updated_at).toLocaleDateString('en-GB') : ''}${group.updated_by ? ` by ${group.updated_by}` : ''}`}
          </p>
        </div>
        <div className="flex gap-1.5">
          {!group.is_default && (
            <Button size="sm" variant="ghost" onClick={reset} disabled={!!busy} className="h-8 text-xs text-gray-600" data-testid={`pack-reset-${group.key}`}>
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Defaults
            </Button>
          )}
          <Button size="sm" onClick={save} disabled={!dirty || !!busy} className="h-8 text-xs bg-[#7A0B7A] hover:bg-[#5e0860] text-white" data-testid={`pack-save-${group.key}`}>
            {busy === 'save' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-gray-500">
            <tr>
              {group.columns.map((c) => <th key={c.field} className="text-left py-1 pr-2 font-semibold">{c.label}{c.unit ? ` (${c.unit})` : ''}</th>)}
              {!group.single && <th className="w-8" />}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="border-t" data-testid={`pack-row-${group.key}-${i}`}>
                {group.columns.map((c) => (
                  <td key={c.field} className="py-1 pr-2">
                    {c.type === 'bool' ? (
                      <input type="checkbox" checked={!!row[c.field]} onChange={(e) => setCell(i, c.field, e.target.checked)} className="h-4 w-4 accent-[#7A0B7A]" data-testid={`pack-cell-${group.key}-${i}-${c.field}`} />
                    ) : (
                      <Input
                        type={c.type === 'number' ? 'number' : 'text'} step={c.field === 'price' ? '0.01' : 'any'} min="0"
                        value={row[c.field] ?? ''} onChange={(e) => setCell(i, c.field, e.target.value)}
                        className={`h-8 text-sm ${c.type === 'number' ? 'w-24 sm:w-28' : 'w-28 sm:w-36'}`}
                        data-testid={`pack-cell-${group.key}-${i}-${c.field}`}
                      />
                    )}
                  </td>
                ))}
                {!group.single && (
                  <td className="py-1">
                    <button type="button" onClick={() => setRows((r) => r.filter((_, idx) => idx !== i))} className="text-gray-400 hover:text-rose-600" aria-label="Remove pack" data-testid={`pack-remove-${group.key}-${i}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!group.single && (
        <button type="button" onClick={() => setRows((r) => [...r, emptyRow(group.columns)])} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#7A0B7A] hover:underline" data-testid={`pack-add-${group.key}`}>
          <Plus className="w-3.5 h-3.5" /> Add pack size
        </button>
      )}
    </Card>
  );
}

export const ProEpoxyPackEditor = () => {
  const [groups, setGroups] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProEpoxyCatalogue().then(setGroups).catch((e) => setError(e.message));
  }, []);

  const sections = useMemo(() => {
    const out = {};
    Object.values(groups || {}).forEach((g) => { (out[g.section] ||= []).push(g); });
    return out;
  }, [groups]);

  if (error) return <p className="text-sm text-rose-600" data-testid="pack-editor-error">{error}</p>;
  if (!groups) return <p className="text-sm text-gray-500">Loading pack catalogue…</p>;

  return (
    <div className="space-y-4" data-testid="proepoxy-pack-editor">
      <div className="flex items-start gap-2">
        <Layers className="w-5 h-5 text-[#7A0B7A] mt-0.5" />
        <div>
          <h2 className="font-bold text-gray-900">Pack sizes, coverage &amp; prices</h2>
          <p className="text-xs text-gray-500">
            These drive the ProEpoxy order builder's automatic pack maths. Add a row for a new kit (e.g. a 15 m² primer), set its coverage and price, and Save — installers see it on their next order.
          </p>
        </div>
      </div>
      {Object.entries(sections).map(([section, list]) => (
        <div key={section}>
          <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">{section}</p>
          <div className="grid gap-3 md:grid-cols-2">
            {list.map((g) => (
              <GroupEditor key={g.key} group={g} onSaved={(updated) => setGroups((prev) => ({ ...prev, [updated.key]: updated }))} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
